import React from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import './DemographicsSection.scss';
import { Tooltip } from '@mui/material';
import { InfoIcon } from 'lucide-react';

// Helper function to calculate Y-axis ticks
const calculateYAxisTicks = (data) => {
  if (!data || data.length === 0) return { max: 10, ticks: [0, 2, 4, 6, 8, 10] };
  
  const allZeros = data.every(item => item.value === 0);
  if (allZeros) {
    return { max: 5, ticks: [1, 2, 3, 4, 5] };
  }
  
  const maxValue = Math.max(...data.map(item => item.value || 0));
  const step = Math.pow(10, Math.floor(Math.log10(maxValue)));
  let maxTick = Math.ceil(maxValue / step) * step;
  
  // Ensure we have at least 5 ticks
  const tickCount = 5;
  const tickStep = Math.max(1, Math.ceil(maxTick / (tickCount - 1) / step) * step);
  maxTick = tickStep * (tickCount - 1);
  
  const ticks = [];
  for (let i = 0; i <= tickCount; i++) {
    ticks.push(i * tickStep);
  }
  
  return { max: maxTick, ticks };
};

const DemographicsSection = ({ locumHoursData, selectedLocation }) => {
  const professionData = [
    { name: 'Nurse', value: Math.round(locumHoursData?.nursePercentage), color: '#034585' },
    { name: 'Doctor', value: Math.round(locumHoursData?.doctorPercentage), color: '#E93B3B' },
    { name: 'Other', value: Math.round(locumHoursData?.otherProfessionalPercentage), color: '#E5E7EB' }
  ];

  const hasProfessionData = professionData.some(item => item.value > 0);
  console.log("hasProfessionData", hasProfessionData);

  const genderData = [
    { name: 'Male', value: Math.round(locumHoursData?.malePercentage || 1), color: '#034585' },
    { name: 'Female', value: Math.round(locumHoursData?.femalePercentage || 1), color: '#F97316' },
    { name: 'Other', value: Math.round(locumHoursData?.otherGenderPercentage || 1), color: '#E5E7EB' }
  ]

  const ageGroupData = [
    { name: '20-30', value: locumHoursData?.ageGroupGraph?.group_20_30 || 0 },
    { name: '31-40', value: locumHoursData?.ageGroupGraph?.group_31_40 || 0 },
    { name: '41-50', value: locumHoursData?.ageGroupGraph?.group_41_50 || 0 },
    { name: '51-60', value: locumHoursData?.ageGroupGraph?.group_51_60 || 0 },
    { name: '60+', value: locumHoursData?.ageGroupGraph?.group_60_plus || 0 }
  ];

  const hasAgeData = ageGroupData.some(item => item.value > 0);
  console.log("hasAgeData", hasAgeData);

  // Check if all values are 0 and replace with static data if true
  const allZeros = ageGroupData.every(item => item.value === 0);
  if (allZeros) {
    ageGroupData.forEach((item, index) => {
      item.value = index + 1;
    });
  }

  const experienceData = [
    { name: '0-3', value: locumHoursData?.experienceGraph?.range_0_3 || 0 },
    { name: '3-6', value: locumHoursData?.experienceGraph?.range_3_6 || 0 },
    { name: '6-10', value: locumHoursData?.experienceGraph?.range_6_10 || 0 },
    { name: '10+', value: locumHoursData?.experienceGraph?.range_10_plus || 0 }
  ].filter(item => item.value !== undefined);

  const hasExperienceData = experienceData.some(item => item.value > 0);
  console.log("hasExperienceData", hasExperienceData);

  // Calculate Y-axis configurations
  const ageGroupYAxis = calculateYAxisTicks(ageGroupData);
  ageGroupYAxis.max = Math.max(ageGroupYAxis.max, 1);
  ageGroupYAxis.ticks = ageGroupYAxis.ticks.length > 0 ? ageGroupYAxis.ticks : [0, 1];

  const experienceYAxis = calculateYAxisTicks(experienceData);

  const renderCustomizedLabel = (data, dataKey) => {
    return data.map((entry, index) => (
      <div key={index} className="demographics__legend-item">
        <div 
          className="demographics__legend-color" 
          style={{ backgroundColor: entry.color }}
        ></div>
        <span className="demographics__legend-text">
          {entry.name} ({entry.value}%)
        </span>
      </div>
    ));
  };

  return (
    <div className="demographics">
      <h2 className="demographics__title">Demographics</h2>
      
      <div className="demographics__grid">
        {/* Profession Distribution - Donut Chart */}
        <div className="demographics__card">
          <div className="demographics__card-header">
          <h3 className="demographics__card-title">Profession Distribution</h3>
          <Tooltip
                        // placement="right"
                        title="Percentage breakdown of professionals by role, such as Nurse, Doctor, and Other professions." arrow
                      >
                        <InfoIcon />
                      </Tooltip>
          </div>
          {/* <p className="demographics__card-subtitle">Breakdown of professionals worked in locum jobs</p> */}
          <div className="demographics__chart demographics__chart--donut">
            {!hasProfessionData ? (
              <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%'}}>
                <h3 className="demographics__card-title">No Profession Data</h3>
                <p style={{textAlign:'center' , fontSize:'0.8rem' , fontWeight:'400' , color:'#1f2937'}}>
                  There is no profession distribution data available for your current selection.
                </p>
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={professionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {professionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="demographics__legend">
                  {renderCustomizedLabel(professionData, 'value')}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Gender Distribution - Donut Chart */}
        
          {/* <div className="demographics__card">
            <div className="demographics__card-header">
              <h3 className="demographics__card-title">Gender Distribution</h3>
              <Tooltip title="Gender Distribution" arrow>
                <InfoIcon />
              </Tooltip>
            </div>
            <div className="demographics__chart demographics__chart--donut">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={genderData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {genderData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="demographics__legend">
                {genderData.map((entry, index) => (
                  <div key={index} className="demographics__legend-item">
                    <div 
                      className="demographics__legend-color" 
                      style={{ backgroundColor: entry.color }}
                    ></div>
                    <span className="demographics__legend-text">
                      {entry.name} ({entry.value}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
   */}

        {/* Age Groups - Bar Chart */}
        <div className="demographics__card">
          <div className="demographics__card-header">

            <h3 className="demographics__card-title">Age Groups</h3>
          <Tooltip
                        // placement="right"
                        title="Number of professionals grouped by their age range." arrow
                      >
              <InfoIcon />
            </Tooltip>
          </div>
          <div className="demographics__chart">
            {!hasAgeData ? (
              <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%'}}>
                <h3 className="demographics__card-title">No Age Data Found</h3>
                <p style={{textAlign:'center' , fontSize:'0.8rem' , fontWeight:'400' , color:'#1f2937'}}>
                  There are no age group records available to display in this chart.
                </p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={ageGroupData}>
                  <XAxis 
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      // fill: '#9CA3AF',
                      fill: 'black',
                       fontSize: 11  , fontWeight:'500'}}
                    // tick={{ fill: '#9CA3AF', fontSize: 11 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      // fill: '#9CA3AF',
                      fill: 'black',
                       fontSize: 11  , fontWeight:'500'}}
                    // tick={{ fill: '#9CA3AF', fontSize: 11 }}
                    domain={[0, ageGroupYAxis.max]}
                    ticks={ageGroupYAxis.ticks}
                  />
                  <RechartsTooltip 
                    formatter={(value) => [value, 'Count']}
                    labelFormatter={(label) => `Age: ${label}`}
                    cursor={{ fill: 'rgba(255, 253, 253, 0)' }}
                  />
                  {!allZeros && (
                    <Bar 
                      dataKey="value" 
                      fill="#034585" 
                      radius={[2, 2, 0, 0]}
                      maxBarSize={20}
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Years of Experience - Bar Chart */}
        <div className="demographics__card">
          <div className="demographics__card-header">
            <h3 className="demographics__card-title">Years of Experience</h3>
            {/* <p className="demographics__card-subtitle">Experience levels of locum professionals</p> */}
            <Tooltip
                          // placement="right"
                          title="Distribution of professionals based on their total work experience in years." arrow
                        >
                          <InfoIcon />
                        </Tooltip>
          </div>
          <div className="demographics__chart">
            {!hasExperienceData ? (
              <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%'}}>
                <h3 className="demographics__card-title">No Experience Data</h3>
                <p style={{textAlign:'center' , fontSize:'0.8rem' , fontWeight:'400' , color:'#1f2937'}}>
                  There is no experience data available for the professionals in this category.
                </p>
              </div>
            ) : (
              <ResponsiveContainer width="90%" height={250}>
                <BarChart data={experienceData} >
                  <XAxis 
                    dataKey="name" 
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      // fill: '#9CA3AF',
                      fill: 'black',
                       fontSize: 11  , fontWeight:'500'}}
                    // tick={{ fill: '#9CA3AF', fontSize: 11 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      // fill: '#9CA3AF',
                      fill: 'black',
                       fontSize: 11  , fontWeight:'500'}}
                    // tick={{ fill: '#9CA3AF', fontSize: 11 }}
                    domain={[0, experienceYAxis.max]}
                    ticks={experienceYAxis.ticks}
                  />
                  <RechartsTooltip 
                    formatter={(value) => [value, 'Count']}
                    labelFormatter={(label) => `Experience: ${label} years`}
                    cursor={{ fill: 'rgba(255, 253, 253, 0)' }}
                  />
                  <Bar 
                    dataKey="value" 
                    fill="#034585" 
                    radius={[2, 2, 0, 0]}
                    maxBarSize={20}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemographicsSection;